import authService from '@/api/authService';
import bucketApi from '@/api/bucketApi';
import userApi from '@/api/userApi';
import Avatar from '@/components/avatar/avatar';
import ErrorAlert from '@/components/errorAlert';
import FileUploader from '@/components/fileUploader';
import { SearchSelectedFilters } from '@/components/header/searchBox/searchSelectedFilters';
import { useIsSearchActive } from '@/components/header/searchBox/useIsSearchActive';
import Money from '@/components/money';
import NoReceiptsYet from '@/components/noReceiptsYet';
import { PageTitle } from '@/components/pageTitle';
import ResponsiveActions from '@/components/responsiveActions';
import { Section } from '@/components/section';
import useFileUploadPicker from '@/hooks/useFileUploadPicker';
import useFileUploadScanner from '@/hooks/useFileUploadScanner';
import BucketListEntry from '@/pages/listView/components/bucketListEntry';
import { Box, Container, Group, Indicator, Skeleton, Stack, Title } from '@mantine/core';
import { IconCamera, IconPlane, IconPlus, IconUpload } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { hasClaim } from 'common/helpers/claimsHelpers';
import type { AggregatedBucket } from 'common/types/aggregatedBucket';
import _, { sumBy } from 'lodash';
import { t, useTranslator } from 'translations';

export const Route = createFileRoute('/_auth/_home/')({
    component: RouteComponent,

    loaderDeps({ search }) {
        return search;
    },

    async loader({ deps }) {
        await bucketApi.byPerson(deps).get();
    },
});

function RouteComponent() {
    const t = useTranslator();
    const search = Route.useSearch();
    const [buckets, error] = bucketApi.byPerson(search).useCache();
    const searchActive = useIsSearchActive();
    const openScanner = useFileUploadScanner();
    const openFilePicker = useFileUploadPicker();

    return (
        <PageTitle title={t('header.listView')}>
            <Box
                css={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <SearchSelectedFilters />

                {error ? (
                    <ErrorAlert title={t('overview.loadError')} error={error} />
                ) : !buckets ? (
                    <Container
                        size="md"
                        css={{
                            flex: 1,
                            width: '100%',
                            padding: 'var(--mantine-spacing-sm)',
                            paddingTop: 'calc(var(--mantine-spacing-md) * 5)',
                            alignContent: 'start',
                            rowGap: 'var(--mantine-spacing-xl)',
                        }}
                    >
                        <Skeleton variant="rectangular" height={100} />
                        <Skeleton variant="rectangular" height={100} />
                        <Skeleton variant="rectangular" height={100} />
                        <Skeleton variant="rectangular" height={100} />
                    </Container>
                ) : (
                    <Container
                        size="md"
                        css={{
                            flex: 1,
                            width: '100%',
                            padding: 'var(--mantine-spacing-sm)',
                            paddingBottom: '5rem',
                            alignContent: 'start',
                            rowGap: 'var(--mantine-spacing-xl)',
                        }}
                    >
                        {_.map(buckets, (buckets, ownerId) => (
                            <PersonSection key={ownerId} ownerId={ownerId} buckets={buckets} />
                        ))}

                        {_.isEmpty(buckets) && !searchActive && <NoReceiptsYet />}
                        {_.isEmpty(buckets) && searchActive && (
                            <Title
                                order={2}
                                css={{
                                    marginTop: 'var(--mantine-spacing-xl)',
                                    display: 'grid',
                                    justifyContent: 'center',
                                    alignContent: 'center',
                                }}
                            >
                                {t('overview.noMatches')}
                            </Title>
                        )}

                        <ResponsiveActions
                            actions={[
                                {
                                    icon: <IconCamera />,
                                    label: t('newReceipt.scan'),
                                    action: openScanner,
                                },
                                {
                                    icon: <IconUpload />,
                                    label: t('newReceipt.upload'),
                                    action: openFilePicker,
                                },
                                {
                                    icon: <IconPlane />,
                                    label: t('header.newTrip'),
                                    action: '/t',
                                },
                            ]}
                            fabIcon={<IconPlus />}
                            showDesktop={false}
                        />
                    </Container>
                )}
            </Box>

            <FileUploader />
        </PageTitle>
    );
}

function PersonSection({ ownerId, buckets }: { ownerId: string; buckets: AggregatedBucket[] }) {
    const [appLogin] = authService.appLogin.useCache();
    const [owner] = userApi.get(ownerId).useCache();

    const todoCount = !appLogin?.user
        ? 0
        : buckets.filter((bucket) =>
              hasClaim(appLogin.user, 'read', {
                  forState: bucket.state,
                  forUser: bucket.ownerId,
              }),
          ).length;

    const openTotal = sumBy(buckets, (bucket) => {
        const total = bucket.state === 'canceled' ? 0 : (bucket.payments?.total ?? 0);
        const paidTotal = bucket.payments?.paidTotal ?? 0;
        return total - paidTotal;
    });

    return (
        <Section
            collapsible
            title={
                <Group>
                    <Indicator
                        color="secondary"
                        size={20}
                        offset={2}
                        label={todoCount.toString()}
                        css={{
                            marginLeft: 'var(--mantine-spacing-sm)',
                            marginRight: 'var(--mantine-spacing-sm)',
                            fontWeight: 'initial',
                        }}
                    >
                        <Avatar userId={ownerId} />
                    </Indicator>

                    <Title order={2} c="var(--mantine-color-text)">
                        {ownerId === appLogin?.user.id
                            ? t('overview.yourExpenses')
                            : t('overview.approvalsFor', { name: owner?.name ?? ownerId })}
                    </Title>
                </Group>
            }
        >
            <Stack>
                {openTotal !== 0 && (
                    <Title order={3} css={{ marginTop: 'var(--mantine-spacing-md)' }}>
                        {t('overview.total')}: <Money amount={openTotal} />
                    </Title>
                )}

                {buckets.map((bucket) => (
                    <BucketListEntry key={bucket.id} bucket={bucket} />
                ))}
            </Stack>
        </Section>
    );
}
